/**
 * Cartão de Desligamento da ficha do funcionário (E10e — integração Convenia):
 * espelho INFORMATIVO do desligamento registrado no Convenia (domínio
 * desligados). Renderiza nada quando não há desligamento espelhado. A rescisão
 * (verbas) é calculada pela Contmega — o sistema não é motor de rescisão.
 * M4.2 (28/07/2026): bloco com o saldo do banco de horas na data do
 * desligamento (CLT art. 59 §3º — relatório informativo; sem baixa automática).
 */
import { useEffect, useState } from "react";
import { Alert, Badge, Card, Group, Text, Title } from "@mantine/core";
import { api } from "../../api";
import { fmtData, fmtHhMm } from "../../format";

interface Desligamento {
  id: string;
  dataDesligamento: string;
  tipoTitulo: string | null;
  avisoTipoNome: string | null;
  etapaNome: string | null;
  motivo: string | null;
  comentarios: string | null;
  quebraContrato: boolean;
  dataRemocaoAcesso: string | null;
  dataContabilidade: string | null;
  removerBeneficio: boolean | null;
  finalizadoEm: string | null;
}

interface SaldoBanco {
  saldoMinutos: number;
  classificacao: "positivo" | "negativo" | "zerado";
  pagaSaldoDesligamentoVigente: boolean;
  orientacao: string;
  alertasReconciliacao: string[];
}

export function DesligamentoCard({ funcionarioId }: { funcionarioId: string }) {
  const [desligamentos, setDesligamentos] = useState<Desligamento[] | null>(null);
  const [saldoBanco, setSaldoBanco] = useState<SaldoBanco | null>(null);

  useEffect(() => {
    api
      .get<{ desligamentos: Desligamento[] }>(`/funcionarios/${funcionarioId}/desligamento`)
      .then((r) => setDesligamentos(r.desligamentos))
      .catch(() => setDesligamentos([])); // falha de leitura não derruba a ficha
    api
      .get<SaldoBanco>(`/funcionarios/${funcionarioId}/desligamento/saldo-banco`)
      .then(setSaldoBanco)
      .catch(() => setSaldoBanco(null)); // sem espelho/data (422) ou falha = sem bloco
  }, [funcionarioId]);

  if (!desligamentos || desligamentos.length === 0) return null;
  const d = desligamentos[0]; // mais recente (ORDER BY data DESC)

  return (
    <Card withBorder shadow="xs" maw={560} mb="md" bg="gray.0">
      <Group justify="space-between" mb="xs">
        <Title order={5}>Desligamento (Convenia)</Title>
        <Badge color="gray" variant="light">
          {d.etapaNome ?? "registrado"}
        </Badge>
      </Group>
      <Text size="sm">
        <strong>{fmtData(d.dataDesligamento)}</strong>
        {d.tipoTitulo ? ` · ${d.tipoTitulo}` : ""}
        {d.avisoTipoNome ? ` · aviso ${d.avisoTipoNome.toLowerCase()}` : ""}
      </Text>
      {(d.motivo || d.comentarios) && (
        <Text size="sm" c="dimmed" mt={4}>
          {[d.motivo, d.comentarios].filter(Boolean).join(" — ")}
        </Text>
      )}
      <Text size="xs" c="dimmed" mt={6}>
        {[
          d.dataContabilidade ? `contabilidade ${fmtData(d.dataContabilidade)}` : null,
          d.dataRemocaoAcesso ? `acesso removido ${fmtData(d.dataRemocaoAcesso)}` : null,
          d.removerBeneficio === true ? "benefício removido" : null,
          d.quebraContrato ? "quebra de contrato" : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Text>
      <Text size="xs" c="dimmed" mt={6}>
        Espelho informativo — a rescisão (verbas) é calculada pela Contmega.
      </Text>
      {saldoBanco && (
        <Alert
          mt="sm"
          variant="light"
          color={saldoBanco.classificacao === "zerado" ? "gray" : saldoBanco.classificacao === "positivo" ? "sillis" : "orange"}
          title={
            saldoBanco.classificacao === "zerado"
              ? "Banco de horas zerado no desligamento"
              : `Saldo do banco de horas no desligamento: ${fmtHhMm(saldoBanco.saldoMinutos)}`
          }
        >
          <Text size="xs">{saldoBanco.orientacao}</Text>
          {saldoBanco.alertasReconciliacao.length > 0 && (
            <Text size="xs" c="orange" mt={6}>
              A fonte (Tique Taque) divergia do ledger no último sync antes do desligamento:
              {saldoBanco.alertasReconciliacao.map((a, i) => (
                <span key={i}>
                  <br />· {a}
                </span>
              ))}
            </Text>
          )}
        </Alert>
      )}
    </Card>
  );
}
